import React from 'react';
import currencyFormat from '@app/utils/currencyFormat';
import { getCarouselSlideLabel } from './postCarouselSlideUtils';

type CarouselMetricSlide = {
    platformPostId?: string | null;
    impressions?: number | null;
    videoViews?: number | null;
    engagements?: number | null;
    fullMediaValue?: number | null;
};

export type CarouselMetricRow = {
    index: number;
    label: string;
    impressions: number;
    videoViews: number;
    engagements: number;
    fullMediaValue: number;
};

type PostCarouselMetricsProps = {
    activeSlideIndex: number;
    currency: string;
    carouselSlides: CarouselMetricSlide[];
    t: (key: string) => string;
    onSelectSlide: (slideIndex: number) => void;
};

const getNumberMetric = (value?: number | null) =>
    typeof value === 'number' ? value : 0;

export const getCarouselMetricRows = (
    slides: CarouselMetricSlide[],
): CarouselMetricRow[] =>
    slides.map((slide, index) => ({
        index,
        label: getCarouselSlideLabel(slide, index),
        impressions: getNumberMetric(slide?.impressions),
        videoViews: getNumberMetric(slide?.videoViews),
        engagements: getNumberMetric(slide?.engagements),
        fullMediaValue: getNumberMetric(slide?.fullMediaValue),
    }));

export const getCarouselMetricTotals = (rows: CarouselMetricRow[]) =>
    rows.reduce(
        (totals, row) => ({
            // Backend grouped-carousel totals use MAX for these post-level fields.
            impressions: Math.max(totals.impressions, row.impressions),
            videoViews: totals.videoViews + row.videoViews,
            engagements: totals.engagements + row.engagements,
            fullMediaValue: Math.max(totals.fullMediaValue, row.fullMediaValue),
        }),
        {
            impressions: 0,
            videoViews: 0,
            engagements: 0,
            fullMediaValue: 0,
        },
    );

const PostCarouselMetrics: React.FC<PostCarouselMetricsProps> = ({
    activeSlideIndex,
    currency,
    carouselSlides,
    t,
    onSelectSlide,
}) => {
    const rows = getCarouselMetricRows(carouselSlides);
    const totals = getCarouselMetricTotals(rows);

    return (
        <div className="modal-v2-carousel-metrics">
            <div className="modal-v2-carousel-metrics-scroll">
                <table className="modal-v2-carousel-metrics-table">
                    <thead>
                        <tr>
                            <th />
                            <th>
                                {t(
                                    'socialMediaExposure.postModal.metrics.impressions',
                                )}
                            </th>
                            <th>
                                {t(
                                    'socialMediaExposure.postModal.metrics.videoViews',
                                )}
                            </th>
                            <th>
                                {t(
                                    'socialMediaExposure.postModal.metrics.engagements',
                                )}
                            </th>
                            <th>
                                {t('socialMediaExposure.postModal.metrics.fmv')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => (
                            <tr
                                className={`modal-v2-carousel-metrics-row${
                                    row.index === activeSlideIndex
                                        ? ' modal-v2-carousel-metrics-row--active'
                                        : ''
                                }`}
                                key={row.index}
                                onClick={() => onSelectSlide(row.index)}
                            >
                                <th scope="row">{row.label}</th>
                                <td>{row.impressions.toLocaleString()}</td>
                                <td>{row.videoViews.toLocaleString()}</td>
                                <td>{row.engagements.toLocaleString()}</td>
                                <td>
                                    {currencyFormat(
                                        row.fullMediaValue,
                                        2,
                                        currency,
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <th scope="row">
                                {t('socialMediaExposure.postModal.total')}
                            </th>
                            <td>{totals.impressions.toLocaleString()}</td>
                            <td>{totals.videoViews.toLocaleString()}</td>
                            <td>{totals.engagements.toLocaleString()}</td>
                            <td>
                                {currencyFormat(
                                    totals.fullMediaValue,
                                    2,
                                    currency,
                                )}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

export default PostCarouselMetrics;
